import { z } from 'zod';
import { idSchema } from './common.js';
/**
 * A meeting already in the signed-in person's Outlook calendar.
 *
 * This is not a reservation and never becomes one on its own. It exists so the
 * application can answer a question it could not answer before: "which of the
 * meetings I already have has nowhere to be held?".
 *
 * The case that motivated it: someone outside the organisation invites a
 * colleague, so the meeting is already in that colleague's calendar. Booking a
 * room used to mean creating a *second* meeting at the same hour — the real one
 * and the one this application wrote — leaving two blocks stacked on the same
 * slot. Reading what is already there is what makes the duplicate unnecessary.
 */
export const myMeetingRoomSchema = z.object({
    roomId: idSchema,
    name: z.string(),
});
export const myMeetingSchema = z.object({
    /** Graph event id, in the caller's own mailbox. */
    id: z.string(),
    /** Stable across mailboxes; how a reservation is tied to this meeting. */
    iCalUId: z.string().nullable(),
    subject: z.string().nullable(),
    startAtUtc: z.string(),
    endAtUtc: z.string(),
    isOrganizer: z.boolean(),
    organizer: z
        .object({ displayName: z.string().nullable(), email: z.string().nullable() })
        .nullable(),
    /** True when the organizer's address is outside the tenant. */
    organizerIsExternal: z.boolean(),
    isOnlineMeeting: z.boolean(),
    /** One of this application's rooms, already attached in Outlook. */
    room: myMeetingRoomSchema.nullable(),
    /**
     * A room attached in Outlook that this application does not manage. Shown as
     * a place, not offered for booking: the meeting already has somewhere to go.
     */
    externalLocation: z.string().nullable(),
    /** The reservation registered here for this meeting, when there is one. */
    reservation: z
        .object({ id: idSchema, roomId: idSchema, roomName: z.string() })
        .nullable(),
});
/** Window to look at. Defaults to the next seven days, capped at 31. */
export const myMeetingsQuerySchema = z
    .object({
    start: z.string().datetime().optional(),
    end: z.string().datetime().optional(),
    /** Only meetings with nowhere to be held; the default view. */
    withoutRoom: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((value) => value === true || value === 'true')
        .optional(),
})
    .refine((value) => !value.start || !value.end || Date.parse(value.end) > Date.parse(value.start), { message: 'El final del rango debe ser posterior al inicio', path: ['end'] });
//# sourceMappingURL=my-meeting.js.map